import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaCalendarAlt,
  FaStore,
  FaExternalLinkAlt,
  FaTicketAlt,
  FaChevronRight,
  // FaHandshake,  // ARCHIVED — used by old engagement section icon
  FaStar,
  FaGavel,
} from "react-icons/fa";
import "./EventDetail.css";
import LoadingPage from "../../components/Loading/LoadingPage";
import { useAuth } from "../../contexts/AuthContext";
import { getEventTiers, getEventAvailability, getLowestDisplayPrice, formatPrice, isEventSoldOut } from "../../services/ticketingService";
import { trackEventRSVPAttempt } from "../../utils/analytics";
import { getCachedData, setCachedData } from "../../utils/dataCache";
import { appendCompanyCode } from "../../hooks/useCompanyCode";
import MediaGallery from "../../components/MediaGallery/MediaGallery";

const TZ = "America/Chicago";

function formatEventDate(startAt, endAt) {
  const startDate = new Date(startAt);
  const endDate = new Date(endAt);

  const formatDate = (date) => {
    const weekday = date.toLocaleDateString("en-US", { weekday: "long", timeZone: TZ });
    const month   = date.toLocaleDateString("en-US", { month: "long",   timeZone: TZ });
    const day     = date.toLocaleDateString("en-US", { day: "numeric",  timeZone: TZ });
    const year    = date.toLocaleDateString("en-US", { year: "numeric", timeZone: TZ });
    const currentYear = new Date().toLocaleDateString("en-US", { year: "numeric", timeZone: TZ });

    // Hide year if it's the current year
    if (year === currentYear) {
      return `${weekday}, ${month} ${day}`;
    }
    return `${weekday}, ${month} ${day} ${year}`;
  };

  const startFormatted = formatDate(startDate);

  // Compare dates in Chicago timezone
  const startDay = startDate.toLocaleDateString("en-US", { timeZone: TZ });
  const endDay   = endDate.toLocaleDateString("en-US", { timeZone: TZ });
  if (startDay === endDay) {
    return startFormatted;
  }

  const endFormatted = formatDate(endDate);
  return `${startFormatted} - ${endFormatted}`;
}

function formatEventTime(startAt, endAt) {
  const startDate = new Date(startAt);
  const endDate = new Date(endAt);

  const timeOptions = { hour: "numeric", minute: "2-digit", hour12: true, timeZone: TZ };
  const startTime = startDate.toLocaleTimeString("en-US", timeOptions);
  const endTime   = endDate.toLocaleTimeString("en-US", timeOptions);

  const startDay = startDate.toLocaleDateString("en-US", { timeZone: TZ });
  const endDay   = endDate.toLocaleDateString("en-US", { timeZone: TZ });

  if (startDay === endDay) {
    return `${startTime} - ${endTime}`;
  }

  // Multi-day event
  return `Starts ${startTime} on ${startDate.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: TZ })}`;
}

function formatStartTime(startAt) {
  const startDate = new Date(startAt);
  return startDate.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: TZ });
}

function formatCompactDate(startAt) {
  const startDate = new Date(startAt);
  const month = startDate.toLocaleDateString("en-US", { month: "short",   timeZone: TZ });
  const day   = startDate.toLocaleDateString("en-US", { day: "numeric",   timeZone: TZ });
  const year  = startDate.toLocaleDateString("en-US", { year: "numeric",  timeZone: TZ });
  return `${month} ${day} ${year}`;
}

function formatMobileDatetime(startAt, endAt) {
  const d = new Date(startAt);
  const timeOpts = { hour: "numeric", minute: "2-digit", hour12: true, timeZone: TZ };
  const month = d.toLocaleDateString("en-US", { month: "short", timeZone: TZ });
  const day   = d.toLocaleDateString("en-US", { day: "numeric", timeZone: TZ });
  const startTime = d.toLocaleTimeString("en-US", timeOpts);
  if (endAt) {
    const endTime = new Date(endAt).toLocaleTimeString("en-US", timeOpts);
    if (endTime !== startTime) return `${month} ${day}  ·  ${startTime} – ${endTime}`;
  }
  return `${month} ${day}  ·  ${startTime}`;
}

function capitalizeEventType(type) {
  if (!type) return "";
  return type
    .split("_")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

export default function EventDetail() {
  const { eventSlug } = useParams();
  const navigate = useNavigate();
  const { setShowLoading, customerToken } = useAuth();

  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";

  const [loading, setLoading] = useState(true);
  const [tiersLoading, setTiersLoading] = useState(true);
  const [ticketsLoading, setTicketsLoading] = useState(true);
  const [contentReady, setContentReady] = useState(false);
  const [event, setEvent] = useState(null);
  const [error, setError] = useState("");
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [showReadMore, setShowReadMore] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);
  // ARCHIVED — countdown was used for old external engagement link flow
  // const [countdown, setCountdown] = useState(null);
  // Tickets the customer already holds for this event
  const [myEventTickets, setMyEventTickets] = useState([]);
  // Lowest currently-purchasable tier price:
  //   undefined = not yet fetched (don't show any price yet)
  //   null      = fetched but no active tiers
  //   number    = price in cents
  const [lowestTierPrice, setLowestTierPrice] = useState(undefined);
  const [isSoldOut, setIsSoldOut] = useState(false);
  const [availableCount, setAvailableCount] = useState(null); // lowest remaining count across capped tiers

  const descriptionRef = useRef(null);
  const reserveBarRef = useRef(null);

  // Check if user is viewing on mobile
  const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(
      window.matchMedia('(max-width: 768px)').matches
    );

    useEffect(() => {
      const media = window.matchMedia('(max-width: 768px)');
      const handler = () => setIsMobile(media.matches);

      media.addEventListener('change', handler);
      return () => media.removeEventListener('change', handler);
    }, []);

    return isMobile;
  };

  const isMobile = useIsMobile();

  // Control header/footer visibility when showing loading page
  useEffect(() => {
    setShowLoading(!contentReady);
  }, [contentReady, setShowLoading]);

  // Set content ready only after event, tiers, and tickets are all resolved
  useEffect(() => {
    if (!loading && !tiersLoading && !ticketsLoading && !contentReady) {
      requestAnimationFrame(() => {
        setContentReady(true);
      });
    }
  }, [loading, tiersLoading, ticketsLoading, contentReady]);

  // Check if description content overflows the collapsed height (200px)
  useEffect(() => {
    if (event?.description && descriptionRef.current) {
      // Wait for next frame to ensure content is fully rendered
      requestAnimationFrame(() => {
        if (descriptionRef.current) {
          const COLLAPSED_MAX_HEIGHT = 200; // Must match CSS .event-description.collapsed max-height
          const actualHeight = descriptionRef.current.scrollHeight;
          setShowReadMore(actualHeight > COLLAPSED_MAX_HEIGHT);
        }
      });
    } else {
      setShowReadMore(false);
    }
  }, [event?.description, contentReady]);

  // Detect when user scrolls to bottom of page to hide the mobile reserve bar
  useEffect(() => {
    if (!isMobile || !contentReady) return;

    const handleScroll = () => {
      // Use clientHeight (stable) instead of innerHeight (changes as browser chrome shows/hides on mobile scroll)
      const windowHeight = document.documentElement.clientHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY || document.documentElement.scrollTop;

      // Consider "at bottom" if within 100px of the bottom
      const threshold = 100;
      const atBottom = windowHeight + scrollTop >= documentHeight - threshold;

      setIsAtBottom(atBottom);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    // Delay initial check so the DOM is fully painted before reading scrollHeight.
    // Without this, on direct load scrollHeight can be near-zero when contentReady
    // first fires, falsely marking isAtBottom=true and hiding the bar.
    const timer = setTimeout(handleScroll, 150);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timer);
    };
  }, [isMobile, contentReady]);

  // Fix Chrome Android gap: when the URL bar hides while scrolling, the layout
  // viewport grows but fixed elements can lag. Use visualViewport to keep the bar
  // flush with the actual visible bottom.
  useEffect(() => {
    if (!isMobile || !window.visualViewport) return;

    const adjust = () => {
      if (!reserveBarRef.current) return;
      const vv = window.visualViewport;
      const gap = window.innerHeight - vv.offsetTop - vv.height;
      reserveBarRef.current.style.bottom = `${Math.max(0, gap)}px`;
    };

    window.visualViewport.addEventListener('resize', adjust);
    window.visualViewport.addEventListener('scroll', adjust);
    adjust();

    return () => {
      window.visualViewport.removeEventListener('resize', adjust);
      window.visualViewport.removeEventListener('scroll', adjust);
    };
  }, [isMobile]);

  // ARCHIVED — countdown timer for old external engagement link flow
  // useEffect(() => {
  //   if (!event?.start_at || !event?.has_engagement_process || !event?.engagement_link_url) return;
  //   const tick = () => {
  //     const diff = new Date(event.start_at) - Date.now();
  //     if (diff <= 0) { setCountdown(null); return; }
  //     const days    = Math.floor(diff / (1000 * 60 * 60 * 24));
  //     const hours   = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  //     const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  //     const seconds = Math.floor((diff % (1000 * 60)) / 1000);
  //     setCountdown({ days, hours, minutes, seconds });
  //   };
  //   tick();
  //   const id = setInterval(tick, 1000);
  //   return () => clearInterval(id);
  // }, [event?.start_at, event?.has_engagement_process, event?.engagement_link_url]);

  useEffect(() => {
    let ok = true;
    (async () => {
      try {
        setLoading(true);
        setContentReady(false);

        if (!eventSlug) {
          setError("Event not found.");
          setLoading(false);
          return;
        }

        // Check cache — skip if missing voting_enabled OR if voting_enabled is false
        // (always re-fetch to catch admin toggling it on)
        const cachedEventData = getCachedData('event', eventSlug);

        if (cachedEventData && cachedEventData.voting_enabled === true) {
          if (ok) {
            setEvent(cachedEventData);
            setLoading(false);
          }
          return;
        }

        // Fetch event details
        const response = await fetch(
          `${apiUrl}/loyalty/events/${eventSlug}`
        );

        if (!response.ok) {
          if (response.status === 404) {
            setError("Event not found.");
          } else {
            setError("Failed to load event details.");
          }
          setLoading(false);
          return;
        }

        const eventData = await response.json();

        if (!ok) return;

        // Cache the event data
        setCachedData('event', eventSlug, eventData);

        setEvent(eventData);
        setLoading(false);
      } catch (e) {
        if (!ok) return;
        setError("Failed to load event details.");
        setLoading(false);
      }
    })();

    return () => {
      ok = false;
    };
  }, [eventSlug, apiUrl]);

  // Fetch tiers + event-level availability (re-runs when customerToken changes)
  useEffect(() => {
    if (!event?.event_id) return;
    setLowestTierPrice(undefined);
    setTiersLoading(true);
    let mounted = true;
    (async () => {
      try {
        const [tiersData, availData] = await Promise.all([
          getEventTiers(event.event_id, customerToken),
          getEventAvailability(event.event_id),
        ]);
        if (!mounted) return;
        setLowestTierPrice(getLowestDisplayPrice(tiersData));
        setIsSoldOut(isEventSoldOut(tiersData));
        // Use max_capacity - tickets_sold from the availability endpoint.
        // tickets_sold includes individual tier tickets + pack tickets (packs_sold × quantity).
        // Only show a remaining count when the event has an explicit max_capacity set.
        setAvailableCount(availData?.tickets_remaining ?? null);
      } catch {
        if (mounted) setLowestTierPrice(null);
      } finally {
        if (mounted) setTiersLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [event?.event_id, customerToken]);

  // Check if logged-in customer already has tickets for this event
  useEffect(() => {
    if (!customerToken || !event?.event_id) {
      setTicketsLoading(false);
      return;
    }
    setTicketsLoading(true);
    let mounted = true;
    (async () => {
      try {
        const res = await fetch(
          `${apiUrl}/ticketing/tickets?customer_token=${encodeURIComponent(customerToken)}`
        );
        if (!res.ok) {
          if (mounted) setTicketsLoading(false);
          return;
        }
        const allTickets = await res.json();
        const eventTickets = allTickets.filter(
          (t) => t.event_id === event.event_id && t.status !== "cancelled"
        );
        if (mounted) {
          setMyEventTickets(eventTickets);
          setTicketsLoading(false);
        }
      } catch {
        if (mounted) setTicketsLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [customerToken, event?.event_id, apiUrl]);

  const handleVenueClick = (venue) => {
    const parts = [venue.address_1, venue.city, venue.state].filter(Boolean);
    const query = encodeURIComponent(parts.length ? parts.join(', ') : venue.venue_name);
    window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, '_blank', 'noopener,noreferrer');
  };

  // Navigate to internal ticket purchase page
  const handleGetTickets = () => {
    const userId = sessionStorage.getItem('loyalty_auth_token') ||
                   localStorage.getItem('loyalty_auth_token');
    trackEventRSVPAttempt(event.event_id, event.title, userId);
    navigate(`/events/${eventSlug}/tickets`);
  };

  if (!contentReady) {
    return <LoadingPage />;
  }


  if (error) {
    return (
      <div className="event-detail-container">
        <div className="event-detail-error">
          <h2>Error</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return null;
  }

  return (
    <div className="event-detail-container">
      {/* Hero Section - MediaGallery Component */}
      <MediaGallery
        galleryUrls={event.gallery_urls}
        fallbackImage={event.hero_image_url}
        tertiaryFallback={
          // Only use default image if event has no gallery and no hero image
          (!event.gallery_urls || event.gallery_urls.length === 0) &&
          !event.hero_image_url
            ? "https://store-galleries.s3.us-east-1.amazonaws.com/gallery/7b62d9646ec243a1acbce4c22cf363e4.jpg"
            : null
        }
        alt={event.title}
        overlayContent={
          <div className="event-hero-content-inner">
            {event.event_type && (
              <div className="event-type-tag-detail">
                {capitalizeEventType(event.event_type)}
              </div>
            )}
            <h1 className="event-title-detail">{event.title}</h1>
          </div>
        }
      />

      {/* Short Summary Below Hero */}
      {event.short_summary && (
        <div className="event-short-summary-section">
          <p className="event-short-summary">{event.short_summary}</p>
        </div>
      )}

      {/* Sponsor Strip */}
      {(event.sponsor_name || event.sponsor_logo_url) && (
        <div className="event-sponsor-bar">
          <div className="event-sponsor-bar-inner">
            <span className="event-sponsor-label">Sponsored by</span>
            <span className="event-sponsor-divider" aria-hidden="true" />
            {event.sponsor_logo_url && (
              <img
                src={event.sponsor_logo_url}
                alt={event.sponsor_name || 'Sponsor'}
                className="event-sponsor-logo"
              />
            )}
            {event.sponsor_name && (
              <span className="event-sponsor-name">{event.sponsor_name}</span>
            )}
          </div>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="event-detail-main-wrapper">
        {/* Left Column - Main Content */}
        <div className="event-detail-content-column">
          {/* Key Information Card */}
          <div className="event-info-card">
            <h2 className="event-info-card-title">Event Details</h2>
            {/* Date & Time - Merged */}
            <div className="event-info-item">
              <div className="event-info-icon">
                <FaCalendarAlt />
              </div>
              <div className="event-info-text">
                <h3>Date & Time</h3>
                <div className="datetime-stacked">
                  <p className="datetime-date">{formatEventDate(event.start_at, event.end_at)}</p>
                  <p className="datetime-time">{formatEventTime(event.start_at, event.end_at)}</p>
                </div>
              </div>
            </div>

            <div
              className={event.primary_venue ? "event-info-item clickable" : "event-info-item"}
              onClick={event.primary_venue ? () => handleVenueClick(event.primary_venue) : undefined}
            >
              <div className="event-info-icon">
                <FaStore />
              </div>
              <div className="event-info-text">
                <h3>Location</h3>
                {event.primary_venue ? (
                  <>
                    <p className="venue-name-link">
                      {event.primary_venue.venue_name}
                      <FaExternalLinkAlt className="external-link-icon" />
                    </p>
                    {event.primary_venue.address_1 && (
                      <p className="venue-address">
                        {event.primary_venue.address_1}
                        {event.primary_venue.city && `, ${event.primary_venue.city}`}
                        {event.primary_venue.state && `, ${event.primary_venue.state}`}
                      </p>
                    )}
                  </>
                ) : (
                  <p>TBD</p>
                )}
              </div>
            </div>

            {/* Hosted by Block Club */}
            {event.is_block_club_hosted && (
              <div className="event-info-item">
                <div className="event-info-icon">
                  <FaTicketAlt />
                </div>
                <div className="event-info-text">
                  <h3>Hosted By</h3>
                  <p>Block Club</p>
                </div>
              </div>
            )}
          </div>

          {/* Description */}
          {event.description && (
            <div className="event-description-section">
              <h2>Overview</h2>
              <div
                ref={descriptionRef}
                className={`event-description event-description-display ${
                  showReadMore && !isDescriptionExpanded ? 'collapsed' : 'expanded'
                }`}
                dangerouslySetInnerHTML={{ __html: event.description }}
              />
              {showReadMore && (
                <button
                  className="read-more-btn"
                  onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                >
                  {isDescriptionExpanded ? 'Read less' : 'Read more'}
                </button>
              )}
            </div>
          )}

          {/* Amenities */}
          {event.amenities && (
            <div className="event-amenities-section">
              <h2>Amenities</h2>
              <p className="event-amenities-text">{event.amenities}</p>
            </div>
          )}

          {/* Event Engagement Section — voting and/or silent auction */}
          {(event.voting_enabled || event.auction_enabled) && (
            <div className="event-engagement-section">
              <div className="event-engagement-header">
                <div className="event-engagement-icon">
                  <FaStar />
                </div>
                <h2>Get Involved</h2>
              </div>
              <p className="event-engagement-description">Join in on the fun! Vote for your favorite pieces and bid to take your favorite home as well! </p>
              <div className="event-engagement-cards">
                {event.voting_enabled && (
                  <button
                    className="event-engagement-card event-engagement-card--vote"
                    onClick={() => navigate(`/events/${eventSlug}/vote`)}
                  >
                    <div className="event-engagement-card-icon">
                      <FaStar />
                    </div>
                    <div className="event-engagement-card-content">
                      <span className="event-engagement-card-title">Vote</span>
                      <span className="event-engagement-card-sub">Cast your vote</span>
                    </div>
                    <FaChevronRight className="event-engagement-card-arrow" />
                  </button>
                )}
                {event.auction_enabled && (
                  <button
                    className="event-engagement-card event-engagement-card--auction"
                    onClick={() => navigate(`/events/${eventSlug}/auction`)}
                  >
                    <div className="event-engagement-card-icon">
                      <FaGavel />
                    </div>
                    <div className="event-engagement-card-content">
                      <span className="event-engagement-card-title">Bid</span>
                      <span className="event-engagement-card-sub">Place your bid</span>
                    </div>
                    <FaChevronRight className="event-engagement-card-arrow" />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Additional Venues (Multi-Stop Events) */}
          {event.additional_venues && event.additional_venues.length > 0 && (
            <div className="event-venues-section">
              <h2>Event Stops</h2>
              <p className="event-venues-subtitle">
                This event visits multiple locations
              </p>
              <div className="event-venues-list">
                {event.additional_venues.map((venue, index) => (
                  <div
                    key={venue.venue_id}
                    className="event-venue-item"
                    onClick={() => handleVenueClick(venue)}
                  >
                    <div className="venue-stop-number">
                      {venue.stop_order || index + 1}
                    </div>
                    <div className="venue-info">
                      <h3>{venue.venue_name}</h3>
                      {venue.address_1 && (
                        <p>
                          {venue.address_1}
                          {venue.city && `, ${venue.city}`}
                        </p>
                      )}
                      {venue.category && (
                        <span className="venue-category">{venue.category}</span>
                      )}
                    </div>
                    <FaChevronRight className="venue-arrow" />
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>

        {/* Right Sidebar - Sticky Reserve Section (Desktop) */}
        <aside className="event-sticky-reserve-sidebar" aria-label="Event reservation details">
          <h2 className="event-sidebar-title">Quick Info</h2>
          <div className="reserve-info-grid">
            {lowestTierPrice !== undefined && (
              <div className="reserve-info-item">
                <span className="reserve-info-label">Price</span>
                <span className={`reserve-info-value ${isSoldOut ? "reserve-info-value--sold-out" : ""}`}>
                  {isSoldOut
                    ? "Sold Out"
                    : lowestTierPrice === null
                      ? "—"
                      : lowestTierPrice === 0
                        ? "Free"
                        : `From ${formatPrice(lowestTierPrice)}`}
                </span>
              </div>
            )}
            {!isSoldOut && availableCount !== null && availableCount <= 5 && (
              <div className="reserve-info-item">
                <span className="reserve-info-label reserve-info-label--almost-sold-out">Almost Sold Out</span>
                <span className="reserve-info-value reserve-info-value--almost-sold-out">
                  Only {availableCount} ticket{availableCount !== 1 ? "s" : ""} left!
                </span>
              </div>
            )}
            <div className="reserve-info-item">
              <span className="reserve-info-label">When</span>
              <div className="reserve-info-value-stacked">
                <span className="reserve-date">{formatCompactDate(event.start_at)}</span>
                <span className="reserve-time">{formatEventTime(event.start_at, event.end_at)}</span>
              </div>
            </div>
          </div>

          {myEventTickets.length > 0 ? (
            <button
              className="reserve-button-sticky reserve-button-sticky--view-tickets"
              onClick={() => navigate(`/my-tickets?customer_token=${customerToken}&event_id=${event.event_id}`)}
            >
              <FaTicketAlt />
              <span>View My Tickets ({myEventTickets.length})</span>
            </button>
          ) : isSoldOut ? (
            <button className="reserve-button-sticky reserve-button-sticky--sold-out" disabled>
              <FaTicketAlt />
              <span>Sold Out</span>
            </button>
          ) : event.ticketing_enabled ? (
            <button
              className="reserve-button-sticky reserve-button-sticky--tickets"
              onClick={() => navigate(`/events/${eventSlug}/tickets`)}
            >
              <FaTicketAlt />
              <span>Get Tickets</span>
            </button>
          ) : (
            <button
              className="reserve-button-sticky reserve-button-sticky--tickets"
              onClick={handleGetTickets}
            >
              <FaTicketAlt />
              <span>Purchase Your Ticket</span>
            </button>
          )}
        </aside>
      </div>

      {/* Mobile Fixed Bottom Bar */}
      {!isAtBottom && (
        <div className="event-mobile-reserve-bar" ref={reserveBarRef}>
          <div className="mobile-reserve-info">
            <div className="mobile-reserve-info-text">
              {isSoldOut ? (
                <div className="mobile-price mobile-price--sold-out">Sold Out</div>
              ) : (
                lowestTierPrice !== undefined && lowestTierPrice !== null && (
                  <div className="mobile-price">
                    {lowestTierPrice === 0 ? "Free" : `From ${formatPrice(lowestTierPrice)}`}
                  </div>
                )
              )}
              {!isSoldOut && availableCount !== null && availableCount <= 5 && (
                <div className="mobile-availability-warning">Almost sold out — only {availableCount} left!</div>
              )}
            </div>
            <div className="mobile-datetime">{formatMobileDatetime(event.start_at, event.end_at)}</div>
          </div>
          {myEventTickets.length > 0 ? (
            <button
              className="mobile-reserve-button mobile-reserve-button--view-tickets"
              onClick={() => navigate(`/my-tickets?customer_token=${customerToken}&event_id=${event.event_id}`)}
            >
              View My Tickets ({myEventTickets.length})
              <FaTicketAlt />
            </button>
          ) : isSoldOut ? (
            <button className="mobile-reserve-button mobile-reserve-button--sold-out" disabled>
              Sold Out
              <FaTicketAlt />
            </button>
          ) : (
            <button
              className={`mobile-reserve-button ${event.ticketing_enabled ? "mobile-reserve-button--tickets" : ""}`}
              onClick={handleGetTickets}
            >
              {event.ticketing_enabled ? "Get Tickets" : "Purchase Your Ticket"}
              <FaTicketAlt />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
